import React, { useMemo } from 'react';
import {
    Button,
    DatePicker,
    Input,
    InputNumber,
    Select,
    Space,
    Switch,
    Typography
} from 'antd';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Text } = Typography;

const SKIP_IN_FORM = new Set([
    'Oborudovanija_i_materiale',
    'Opisanie_vepolneneh_rabot'
]);

const toDayjs = value => {
    if (!value) return null;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
};

const formatReadonly = (field, value) => {
    if (value == null || value === '') {
        return '—';
    }
    if (field.value_type === 'boolean') {
        return value ? 'Да' : 'Нет';
    }
    if (
        field.value_type === 'date' ||
        field.value_type === 'datetime'
    ) {
        const d = dayjs(value);
        return d.isValid()
            ? d.format(
                  field.value_type === 'date'
                      ? 'DD.MM.YYYY'
                      : 'DD.MM.YYYY HH:mm'
              )
            : String(value);
    }
    if (Array.isArray(value)) {
        return value.join(', ');
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
};

const FieldControl = ({ field, value, onChange, disabled }) => {
    const type = field.value_type || 'string';

    if (disabled) {
        return (
            <Text className="installer-field-readonly">
                {formatReadonly(field, value)}
            </Text>
        );
    }

    if (type === 'boolean') {
        return (
            <Switch
                checked={Boolean(value)}
                onChange={onChange}
            />
        );
    }

    if (type === 'number') {
        return (
            <InputNumber
                style={{ width: '100%' }}
                value={value}
                onChange={onChange}
            />
        );
    }

    if (type === 'date' || type === 'datetime') {
        return (
            <DatePicker
                style={{ width: '100%' }}
                showTime={type === 'datetime'}
                value={toDayjs(value)}
                format={
                    type === 'date'
                        ? 'DD.MM.YYYY'
                        : 'DD.MM.YYYY HH:mm'
                }
                onChange={date =>
                    onChange(
                        date
                            ? type === 'datetime'
                                ? date.toISOString()
                                : date.format('YYYY-MM-DD')
                            : null
                    )
                }
            />
        );
    }

    if (type === 'text') {
        return (
            <TextArea
                rows={3}
                value={value ?? ''}
                onChange={e => onChange(e.target.value)}
            />
        );
    }

    if (
        type === 'select' &&
        Array.isArray(field.possible_values) &&
        field.possible_values.length
    ) {
        return (
            <Select
                allowClear
                style={{ width: '100%' }}
                value={value ?? undefined}
                options={field.possible_values.map(item => ({
                    value: item,
                    label: item
                }))}
                onChange={onChange}
            />
        );
    }

    if (type === 'dictionary' || type === 'composite' || type === 'repeater') {
        return (
            <Text className="installer-field-readonly">
                {formatReadonly(field, value)}
            </Text>
        );
    }

    return (
        <Input
            value={value ?? ''}
            onChange={e => onChange(e.target.value)}
        />
    );
};

const InstallerTaskFields = ({
    fields,
    values,
    onChange,
    onSave,
    saving,
    reportMode
}) => {
    const grouped = useMemo(() => {
        const map = new Map();
        (fields || [])
            .filter(field => !SKIP_IN_FORM.has(field.system_name))
            .filter(field => {
                if (
                    reportMode &&
                    field.system_name === 'Opisanie_vepolneneh_rabot'
                ) {
                    return false;
                }
                return true;
            })
            .forEach(field => {
                const key =
                    field.group?.id ||
                    field.group_id ||
                    'other';
                const title =
                    field.group?.human_name || 'Поля заявки';
                if (!map.has(key)) {
                    map.set(key, {
                        id: key,
                        title,
                        sort:
                            field.group?.sort_order ??
                            9999,
                        items: []
                    });
                }
                map.get(key).items.push(field);
            });
        return Array.from(map.values()).sort(
            (a, b) => a.sort - b.sort
        );
    }, [fields, reportMode]);

    const dirty = useMemo(() => {
        return (fields || []).some(field => {
            if (!field.can_edit) return false;
            if (SKIP_IN_FORM.has(field.system_name)) return false;
            return values[field.system_name] !== field.value;
        });
    }, [fields, values]);

    if (!grouped.length) {
        return null;
    }

    return (
        <div className="installer-task-fields">
            <div className="installer-task-fields__head">
                <Typography.Title level={5} style={{ margin: 0 }}>
                    Данные заявки
                </Typography.Title>
                {dirty ? (
                    <Button
                        size="small"
                        type="primary"
                        loading={saving}
                        onClick={onSave}
                    >
                        Сохранить поля
                    </Button>
                ) : null}
            </div>

            {grouped.map(group => (
                <div key={group.id} className="installer-task-fields__group">
                    <Text
                        type="secondary"
                        className="installer-task-fields__group-title"
                    >
                        {group.title}
                    </Text>
                    <div className="installer-task-fields__grid">
                        {group.items.map(field => (
                            <label
                                key={field.system_name}
                                className={`installer-task-field${
                                    field.value_type === 'text' ||
                                    field.value_type === 'boolean'
                                        ? ' installer-task-field--wide'
                                        : ''
                                }`}
                            >
                                <span className="installer-task-field__label">
                                    {field.human_name ||
                                        field.system_name}
                                    {!field.can_edit ? (
                                        <Text
                                            type="secondary"
                                            style={{
                                                marginLeft: 6,
                                                fontSize: 11
                                            }}
                                        >
                                            только просмотр
                                        </Text>
                                    ) : null}
                                </span>
                                <FieldControl
                                    field={field}
                                    value={
                                        values[field.system_name]
                                    }
                                    disabled={!field.can_edit}
                                    onChange={next =>
                                        onChange(
                                            field.system_name,
                                            next
                                        )
                                    }
                                />
                            </label>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default InstallerTaskFields;
